import { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

const FAQs = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      q: 'How are startups shortlisted for pitching?',
      a: 'Once we receive the nominations, our team reviews the submitted information and conducts due diligence. Startups that qualify will be notified via email or phone. Tip: To improve your chances of being shortlisted, we recommend submitting your pitch deck along with your nomination.'
    },
    {
      q: 'Is there a fee to participate?',
      a: 'There is no fee to submit your nomination. However, if your startup is shortlisted, we will share the payment process required to confirm your participation in the pitching round.'
    },
    {
      q: 'How long is the pitch session?',
      a: 'Each startup is allocated approximately 15–20 minutes for their presentation, followed by a Q&A session with the investors.'
    },
    {
      q: 'How will my pitch be evaluated?',
      a: 'Startups will be evaluated based on a combined score from two main criteria:\n• Business model\n• Presentation skills'
    },
    {
      q: 'What happens after the event?',
      a: 'After the pitching session, investors who express interest in any startup will be connected with them. The startup can then continue discussions with the investor offline.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section 
      id="faqs" 
      className="relative min-h-screen flex items-center py-16 px-4 sm:px-6 bg-gradient-to-b from-secondary via-secondary/95 to-primary/10 overflow-hidden scroll-mt-16"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-bluePrimary/5 rounded-full blur-3xl sm:w-96 sm:h-96"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl sm:w-[500px] sm:h-[500px]"></div>
        <div className="absolute top-1/3 right-1/3 w-48 h-48 bg-green-500/5 rounded-full blur-3xl sm:w-64 sm:h-64"></div>
      </div>

      {/* Decorative Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="relative w-full max-w-5xl mx-auto z-10">
        
        {/* Header Section */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center justify-center gap-3 mb-4 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-bluePrimary/10 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6 text-bluePrimary" />
            </div>
            <span className="text-bluePrimary font-semibold tracking-wider text-xs sm:text-sm uppercase">
              FAQs
            </span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            Frequently Asked <span className="text-bluePrimary">Questions</span>
          </h2>
          
          <p className="text-base sm:text-lg md:text-xl text-textMuted max-w-2xl mx-auto leading-relaxed px-4">
            Everything you need to know about the startup pitch event. 
            Can't find what you're looking for?{' '}
            <a 
              href="#contact-us" 
              className="text-bluePrimary font-medium hover:text-bluePrimary/80 transition-colors hover:underline"
            >
              Contact our team
            </a>
          </p>
        </div>

        {/* Stacked FAQ Accordion */}
        <div className="space-y-4 sm:space-y-6 mb-12 sm:mb-16 px-2 sm:px-0">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`relative overflow-hidden rounded-xl sm:rounded-2xl border transition-all duration-300 ${
                openIndex === index 
                  ? 'border-bluePrimary/30 bg-gradient-to-br from-tertiary to-secondary shadow-[0_10px_40px_rgba(59,130,246,0.15)]' 
                  : 'border-white/10 bg-tertiary/50 hover:bg-tertiary/70 hover:border-white/20 hover:shadow-lg'
              }`}
            >
              {/* FAQ Content */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full text-left p-4 sm:p-6 md:p-8 flex items-start justify-between group"
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <div className="flex-1 pr-8 sm:pr-12">
                  <div className="flex items-start gap-3 sm:gap-4">
                    {/* Question Number */}
                    <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      openIndex === index 
                        ? 'bg-bluePrimary' 
                        : 'bg-white/10 group-hover:bg-bluePrimary/20'
                    }`}>
                      <span className={`text-sm sm:text-lg font-bold ${
                        openIndex === index ? 'text-white' : 'text-textMuted group-hover:text-bluePrimary'
                      }`}>
                        {index + 1}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className={`text-lg sm:text-xl font-semibold mb-1 sm:mb-2 transition-colors ${
                        openIndex === index ? 'text-white' : 'text-white/90 group-hover:text-white'
                      }`}>
                        {faq.q}
                      </h3>
                      
                      <div 
                        id={`faq-answer-${index}`}
                        className={`overflow-hidden transition-all duration-500 ease-in-out ${
                          openIndex === index ? 'max-h-96 opacity-100 mt-3 sm:mt-4' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="text-sm sm:text-base text-textMuted/80 leading-relaxed whitespace-pre-line pl-2 border-l-2 border-bluePrimary/20">
                          {faq.a}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chevron Icon */}
                <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full border flex items-center justify-center transition-all duration-300 ml-2 ${
                  openIndex === index 
                    ? 'bg-bluePrimary border-bluePrimary transform rotate-180' 
                    : 'border-white/20 group-hover:border-bluePrimary/50 group-hover:bg-bluePrimary/10'
                }`}>
                  <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors ${
                    openIndex === index ? 'text-white' : 'text-textMuted group-hover:text-bluePrimary'
                  }`} />
                </div>
              </button>

              {/* Bottom Decorative Line */}
              <div className={`h-0.5 sm:h-1 w-full transition-all duration-500 ${
                openIndex === index 
                  ? 'bg-gradient-to-r from-bluePrimary via-bluePrimary/50 to-transparent opacity-100' 
                  : 'bg-white/5 opacity-0'
              }`}></div>
            </div>
          ))}
        </div>

        
           
        

      </div>
    </section>
  );
};

export default FAQs;